export function formatFileSize(bytes: number): string {
  return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
}

export function formatFileSizeMb(mb: number): string {
  return mb.toFixed(2) + ' MB';
}

export function pluralize(count: number, singular: string, plural?: string): string {
  return count === 1 ? singular : (plural ?? singular + 's');
}

export const shortList = (items: readonly string[], sep = ', ') =>
  items.length <= 3 ? items.join(sep) : `${items.slice(0, 2).join(sep)} +${items.length - 2}`;

/** Short human date, e.g. "5 Sept 2026". Returns "—" for a missing value. */
export function formatShortDate(iso: string | undefined | null): string {
  return iso
    ? new Date(iso).toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : '—';
}

const NAMED_ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
};

export function decodeHtmlEntities(input: string | null | undefined): string {
  if (!input) return '';
  return input.replace(/&(#x?[0-9a-fA-F]+|[a-zA-Z][a-zA-Z0-9]*);/g, (match, body: string) => {
    if (body[0] === '#') {
      const isHex = body[1] === 'x' || body[1] === 'X';
      const code = parseInt(body.slice(isHex ? 2 : 1), isHex ? 16 : 10);
      if (Number.isNaN(code) || code < 0 || code > 0x10ffff) return match;
      return String.fromCodePoint(code);
    }
    return NAMED_ENTITIES[body.toLowerCase()] ?? match;
  });
}

/** Remove a document extension (.pdf, .rtf — case-insensitive) from a filename. */
export function stripDocumentExtension(filename: string): string {
  return filename.replace(/\.(pdf|rtf)$/i, '');
}

/**
 * Returns a new File with the given name (preserving the original file's
 * extension) if the trimmed name differs from the original, otherwise the
 * original File unchanged.
 */
export function applyFileRename(file: File, editedName: string): File {
  const trimmed = stripDocumentExtension(editedName.trim());
  const extMatch = file.name.match(/\.(pdf|rtf)$/i);
  const ext = extMatch ? extMatch[0] : '';
  if (trimmed && `${trimmed}${ext}` !== file.name) {
    return new File([file], `${trimmed}${ext}`, { type: file.type });
  }
  return file;
}

/** Format a duration in milliseconds to a human-readable string (e.g. "3s", "2m 15s"). */
export function formatDurationMs(ms: number): string {
  const secs = Math.max(0, Math.round(ms / 1000));
  return secs < 60 ? `${secs}s` : `${Math.floor(secs / 60)}m ${secs % 60}s`;
}
